import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { apiUtils } from '../../lib/api'

export interface Project {
  id: string
  name: string
  description?: string
  status: string
  createdAt: string
  updatedAt: string
  organizationId: string
}

interface ProjectState {
  currentProject: Project | null
  projects: Project[]
  isLoading: boolean
  error: string | null
}

const initialState: ProjectState = {
  currentProject: null,
  projects: [],
  isLoading: false,
  error: null,
}

// Async thunks
export const fetchProjects = createAsyncThunk(
  'project/fetchProjects',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiUtils.backend.get('/projects')
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch projects')
    }
  }
)

export const createProject = createAsyncThunk(
  'project/createProject',
  async (data: { name: string; description?: string }, { rejectWithValue }) => {
    try {
      const response = await apiUtils.backend.post('/projects', data)
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create project')
    }
  }
)

export const getCurrentUserProject = createAsyncThunk(
  'project/getCurrentUserProject',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiUtils.backend.get('/projects/user/current-project')
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to get current project')
    }
  }
)

export const setCurrentUserProject = createAsyncThunk(
  'project/setCurrentUserProject',
  async (projectId: string, { rejectWithValue }) => {
    try {
      const response = await apiUtils.backend.put(`/projects/user/current-project/${projectId}`)
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to set current project')
    }
  }
)

const projectSlice = createSlice({
  name: 'project',
  initialState,
  reducers: {
    setCurrentProject: (state, action: PayloadAction<Project | null>) => {
      state.currentProject = action.payload
      // Store in localStorage for immediate persistence
      if (action.payload) {
        localStorage.setItem('currentProjectId', action.payload.id)
      } else {
        localStorage.removeItem('currentProjectId')
      }
    },
    clearError: (state) => {
      state.error = null
    },
    loadPersistedProject: (state) => {
      // Load from localStorage on app start
      const persistedProjectId = localStorage.getItem('currentProjectId')
      if (persistedProjectId && state.projects.length > 0) {
        const project = state.projects.find(p => p.id === persistedProjectId)
        if (project) {
          state.currentProject = project
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch projects
      .addCase(fetchProjects.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.isLoading = false
        state.projects = action.payload
        
        // Try to load persisted project first
        const persistedProjectId = localStorage.getItem('currentProjectId')
        let persistedProject = null
        
        if (persistedProjectId) {
          persistedProject = action.payload.find((p: Project) => p.id === persistedProjectId)
        }
        
        if (persistedProject) {
          // Use persisted project if it exists
          state.currentProject = persistedProject
        } else if (!state.currentProject && action.payload.length > 0) {
          // Auto-select first project if none selected and projects exist
          state.currentProject = action.payload[0]
          localStorage.setItem('currentProjectId', action.payload[0].id)
        }
        
        // If current project is no longer in the list, clear it or select first
        if (state.currentProject && !action.payload.find((p: Project) => p.id === state.currentProject!.id)) {
          state.currentProject = action.payload.length > 0 ? action.payload[0] : null
          if (state.currentProject) {
            localStorage.setItem('currentProjectId', state.currentProject.id)
          } else {
            localStorage.removeItem('currentProjectId')
          }
        }
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      
      // Create project
      .addCase(createProject.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(createProject.fulfilled, (state, action) => {
        state.isLoading = false
        state.projects = [action.payload, ...state.projects]
        // Auto-select the newly created project and persist it
        state.currentProject = action.payload
        localStorage.setItem('currentProjectId', action.payload.id)
      })
      .addCase(createProject.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      
      // Get current user project
      .addCase(getCurrentUserProject.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(getCurrentUserProject.fulfilled, (state, action) => {
        state.isLoading = false
        if (action.payload.currentProject) {
          state.currentProject = action.payload.currentProject
          localStorage.setItem('currentProjectId', action.payload.currentProject.id)
        }
      })
      .addCase(getCurrentUserProject.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      
      // Set current user project
      .addCase(setCurrentUserProject.fulfilled, (state, action) => {
        if (action.payload.currentProject) {
          state.currentProject = action.payload.currentProject
          localStorage.setItem('currentProjectId', action.payload.currentProject.id)
        }
      })
  },
})

export const { setCurrentProject, clearError, loadPersistedProject } = projectSlice.actions
export default projectSlice.reducer